import { selectSessionState } from "@/features/sessionSlice";
import { useAppSelector } from "@/hooks/redux";
import { SESSION_STATE } from "@/session/session-state";
import { useBlocker } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * Custom hook that blocks navigation only when user tries to leave the site
 * (close tab, refresh, or navigate to external URL) while in an active session.
 * Internal navigation within the portal is always allowed.
 */
export function useSessionNavigationBlocking() {
  const sessionState = useAppSelector(selectSessionState);

  // Only block when user is actively in a session (ACTIVE or READY state)
  const isInActiveSession =
    sessionState.state === SESSION_STATE.ACTIVE ||
    sessionState.state === SESSION_STATE.READY;

  const shouldBlock = useCallback(() => {
    // We never block internal navigation
    // The beforeunload handler will take care of blocking when leaving the site
    return false;
  }, []);

  // Use TanStack Router's useBlocker hook
  // We're primarily using this for the enableBeforeUnload feature
  const blocker = useBlocker({
    shouldBlockFn: shouldBlock,
    // Enable beforeunload handler when there's an active session
    // This shows a browser confirmation when:
    // - Closing the tab
    // - Refreshing the page
    // - Navigating to an external URL
    enableBeforeUnload: isInActiveSession,
  });

  return blocker;
}
